// @flow
/* globals google */
import _debug from 'debug';
import toggleArray from '@justpark/helpers/src/toggleArray/toggleArray';
import locationDetailReviews from '@justpark/api/src/requests/getLocationDetailReviews';
import walkingDistanceRequest from '@justpark/api/src/requests/getWalkingDistance';
import geoSearchRequest from '@justpark/api/src/requests/getSearchGeoLocation';
import getRequestError from '@justpark/api/src/helpers/getRequestError';
import CancelToken from '@justpark/api/src/cancelToken';
import calculateDateDiff from '@justpark/helpers/src/calculateDateDiff/calculateDateDiff';
import {
  createSelector,
  createSlice,
  createAsyncThunk,
  PayloadAction
} from '@reduxjs/toolkit';
import { milesToMeters, makeActionCreator } from '../helpers/utils';
import { WALKING_METERS_PER_MIN } from '../helpers/maps';
import { formatDurationFromSeconds } from '../helpers/date';
import { isReservable } from '../helpers/location';
import { gtagTrack } from '../helpers/Tracking/Analytics';
import convertToDayjs from '../helpers/dates/convertToDayjs';
import { selectById } from './launchDarkly';

const debug = _debug('jp:reducers:search');

type LatLng = {
  lat: number,
  lng: number
};

type ParkingType = 'onstreet' | 'offstreet';

export type Result = {
  coordinates: LatLng,
  id: number,
  photos: Array<string>,
  price: {
    amount: number,
    currency: string,
    formatted: string
  },
  reviews: {
    rating: number,
    count: number
  },
  title: string,
  type: ParkingType,
  slug?: string,
  cluster?: boolean,
  availability: ?number,
  distance: number,
  occupancyBucket: string,
  unavailable: boolean
};

type SearchFormData = {
  q: string,
  coords: LatLng,
  placeId: string
};

const distance = (
  { lat: lat1, lng: lng1 }: LatLng,
  { coordinates: { lat: lat2, lng: lng2 } }: { coordinates: LatLng }
) => {
  const deglen = 110.25;
  const x = lat1 - lat2;
  const y = (lng1 - lng2) * Math.cos(lat2);
  return deglen * Math.sqrt(x * x + y * y);
};

const MODAL_VISIBLE = 'VISIBLE';
const MONTHLY_UPGRADE_MODAL_VISIBLE = 'MONTHLY_UPGRADE_MODAL_VISIBLE';
const MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE =
  'MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE';
const MODAL_DISMISSED = 'DISMISSED';

export type State = {|
  hoveredId: number,
  datePickerVisible: boolean,
  showFilterPanel: boolean,
  showHowItWorks: boolean,
  loadingPins: boolean,
  showReviews: boolean,
  reviews: {
    [number]: Array<any>
  },
  pinResults: Array<Result>,
  hasSearched: SearchFormData,
  clientSideSearch: boolean,
  showEditSearch: boolean,
  filterResults: Array<Result>,
  filterCheckboxes: Array<string>,
  metadata: {
    title: string,
    description: string,
    canonical_url: string,
    bookingType: string,
    hasOccupancyData: boolean
  },
  currentDistanceFromCenter: number,
  currentPanPosition: { lat: number, lng: number },
  searchTimestamp: number,
  firstLoadComplete: boolean,
  walkingTimesLoaded: boolean,
  monthlyUpgradeModal: null | 'DISMISSED' | 'VISIBLE',
  instantBookModal: null | 'DISMISSED' | 'VISIBLE',
  hasUpdatedFilterQuery: boolean,
  dismissedOccupancySearchKey: boolean,
  walkingTimes: { [number]: number },
  loadPinsError: string
|};

export const defaultCoords = {
  lat: 51.48918,
  lng: -0.212642
};

export const initialState: () => State = () => ({
  selectedId: 0,
  hoveredId: 0,
  searchTimestamp: 0,
  datePickerVisible: false,
  showLocationDetails: false,
  showFilterPanel: false,
  selectedPinDirty: false,
  showHowItWorks: false,
  loadingPins: true,
  showEditSearch: false,
  showReviews: false,
  reviews: {},
  pinResults: [],
  hasSearched: {
    q: '',
    placeId: '',
    coords: defaultCoords
  },
  clientSideSearch: false,
  filterResults: [],
  filterCheckboxes: [],
  metadata: {
    title: '',
    description: '',
    canonical_url: '',
    bookingType: '',
    hasOccupancyData: false
  },
  currentDistanceFromCenter: 0,
  currentPanPosition: {
    lat: 51.48918,
    lng: -0.212642
  },
  firstLoadComplete: false,
  walkingTimesLoaded: false,
  monthlyUpgradeModal: null,
  instantBookModal: null,
  hasUpdatedFilterQuery: false,
  dismissedOccupancySearchKey: false,
  walkingTimes: {},
  loadPinsError: ''
});

const HOVER_ITEM_ENTER = 'search/HOVER_ITEM_ENTER';
const HOVER_ITEM_EXIT = 'search/HOVER_ITEM_EXIT';
export const TOGGLE_FILTER_PANEL = 'search/TOGGLE_FILTER_PANEL';
export const SEARCH_AREA = 'search/SEARCH_AREA';
const SET_AREA = 'search/SET_AREA';
const CLOSE_DETAILS = 'search/CLOSE_DETAILS';
const PANNED = 'search/PANNED';
const SET_DATE = 'search/SET_DATE';
const SET_MONTHLY_UPGRADE_MESSAGE = 'search/SET_MONTHLY_UPGRADE_MESSAGE';
const DISMISS_MONTHLY_UPGRADE_MESSAGE =
  'search/DISMISS_MONTHLY_UPGRADE_MESSAGE';
export const TOGGLE_FILTER_CHECKBOX = 'search/TOGGLE_FILTER_CHECKBOX ';
export const CLEAR_FILTER_CHECKBOXES = 'search/CLEAR_FILTER_CHECKBOXES ';
export const SET_FILTER_CHECKBOXES = 'search/SET_FILTER_CHECKBOXES ';
export const HAS_UPDATED_FILTER_QUERY = 'search/HAS_UPDATED_FILTER_QUERY';
export const DISMISS_OCCUPANCY_SEARCH_KEY =
  'search/DISMISS_OCCUPANCY_SEARCH_KEY';

export type Action =
  | {| type: typeof HOVER_ITEM_ENTER, id: number |}
  | {| type: typeof HAS_UPDATED_FILTER_QUERY |}
  | {| type: typeof DISMISS_MONTHLY_UPGRADE_MESSAGE |}
  | {| type: typeof TOGGLE_DATEPICKER |}
  | {| type: typeof TOGGLE_HOW_IT_WORKS |}
  | {| type: typeof TOGGLE_FILTER_CHECKBOX |}
  | {| type: typeof CLEAR_FILTER_CHECKBOXES |}
  | {| type: typeof SET_FILTER_CHECKBOXES |}
  | {| type: typeof TOGGLE_FILTER_PANEL |}
  | {| type: typeof SET_AREA, area: any |}
  | {| type: typeof SEARCH_AREA, area: SearchFormData |}
  | {| type: typeof CLOSE_DETAILS |}
  | {| type: typeof PANNED, payload: { lat: number, lng: number } |}
  | {|
      type: typeof SET_DATE,
      payload: Array<{
        arriving: Date,
        leaving: ?Date,
        monthlyDuration: ?string
      }>
    |}
  | {| type: typeof DISMISS_OCCUPANCY_SEARCH_KEY |};

export const splitCoords = (coords) =>
  coords.split(',').map((v) => parseFloat(v));

const getInstantBookModalState = (state: State, isMonthly) => {
  if (state.monthlyUpgradeModal === null && !isMonthly) {
    return MODAL_VISIBLE;
  }

  return null;
};

const getMonthlyUpgradeModalState = (state: State, dates) => {
  const { arriving, leaving } = dates;

  if (leaving === null) {
    return state.monthlyUpgradeModal;
  }

  const dayDiff = calculateDateDiff(arriving, leaving);

  if (dayDiff < 25 && state.monthlyUpgradeModal !== MODAL_DISMISSED) {
    return null;
  }

  if (
    dayDiff >= 25 &&
    dayDiff < 90 &&
    state.monthlyUpgradeModal !== MODAL_DISMISSED
  ) {
    return MONTHLY_UPGRADE_MODAL_VISIBLE;
  }

  if (dayDiff >= 90) {
    return MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE;
  }

  return state.monthlyUpgradeModal;
};

export const loadReviews = createAsyncThunk(
  'search/loadReviews',
  async (payload, { rejectWithValue, getState, extra: { jpApiClient } }) => {
    try {
      const { id, show = true } = payload;
      const item = getState().search.pinResults.filter((r) => r.id === id)[0];

      if (item?.reviews.count === 0) {
        return {
          id,
          show,
          data: null
        };
      }

      const request = locationDetailReviews(id);
      const { data } = await jpApiClient(request);
      return {
        id,
        show,
        data: data.data
      };
    } catch (e) {
      return rejectWithValue(getRequestError(e));
    }
  }
);

export const loadWalkingDistance = createAsyncThunk(
  'search/loadWalkingDistance',
  async (payload, { rejectWithValue, getState, extra: { jpApiClient } }) => {
    try {
      const {
        search: {
          pinResults,
          hasSearched: { coords }
        }
      } = getState();

      const listingIds = pinResults.map((p) => p.id);

      if (listingIds.length === 0) {
        return null;
      }

      const request = walkingDistanceRequest(coords, listingIds);
      const { data } = await jpApiClient(request);

      return data.data;
    } catch (e) {
      return rejectWithValue(getRequestError(e));
    }
  }
);

const shouldIncludeSoldOut = (state, monthlyDuration) => {
  try {
    const soldOutFeature = selectById(state, 'show-sold-out');

    const soldOutFeatureEnabled =
      window.Cypress || soldOutFeature?.value === true;

    return soldOutFeatureEnabled && !monthlyDuration;
  } catch (e) {
    return false;
  }
};

export const loadPins = createAsyncThunk(
  'search/loadPins',
  async (
    payload,
    { rejectWithValue, getState, extra: { jpApiClient }, signal }
  ) => {
    try {
      const source = CancelToken.source();

      signal.addEventListener('abort', () => {
        source.cancel();
      });

      const { dates, coords, placeId, q, filters } = payload;

      const state = getState();
      const { geolocation } = state;

      const geoCoords = geolocation?.coordinates;

      const { arriving, leaving, monthlyDuration, monthlyType } = dates;

      const dateFormat = 'YYYY-MM-DD HH:mm';
      const startDate = convertToDayjs(arriving).format(dateFormat);
      const endDate = leaving
        ? convertToDayjs(leaving).format(dateFormat)
        : null;

      const endOrDuration = endDate
        ? { end_date: endDate }
        : { duration: String(monthlyDuration) };

      const includeSoldOut = shouldIncludeSoldOut(state, monthlyDuration);

      const data = {
        lat: coords.lat,
        lng: coords.lng,
        start_date: startDate,
        ...(placeId ? { google_place_id: placeId } : {}),
        user_query: q,
        ...(filters?.length ? { filters } : {}),
        ...(geoCoords?.lat ? { client_location_lat: geoCoords.lat } : {}),
        ...(geoCoords?.lng ? { client_location_lng: geoCoords.lng } : {}),
        ...endOrDuration,
        ...(monthlyType
          ? { monthly_applicable_days: monthlyType.toLowerCase() }
          : {}),
        ...(monthlyDuration ? { show_next_available_days: 1 } : {}),
        ...(includeSoldOut ? { show_sold_out: 1 } : {})
      };

      const request = geoSearchRequest(data, source);
      const response = await jpApiClient(request);

      return { ...response.data, coords, placeId, q };
    } catch (e) {
      return rejectWithValue(getRequestError(e));
    }
  },
  {
    condition: (payload) => {
      const { dates } = payload;
      const { arriving, leaving } = dates;

      const dateDiff = calculateDateDiff(arriving, leaving);

      if (dateDiff >= 90) {
        return false;
      }

      return true;
    }
  }
);

const sliceName = 'search';

const searchSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    setInstantBookMessage(state: State, action: PayloadAction) {
      const monthlyDurationParam = action.payload?.monthly_duration;
      const instantBookModal = getInstantBookModalState(
        state,
        monthlyDurationParam
      );

      return {
        ...state,
        instantBookModal
      };
    },
    dismissInstantBookMessage(state: State) {
      return {
        ...state,
        instantBookModal: MODAL_DISMISSED
      };
    },
    reset: () => initialState()
  },
  extraReducers: {
    [PANNED](state, action) {
      debug(PANNED, action);
      const { lat, lng } = state.hasSearched.coords;
      const { payload } = action;
      return {
        ...state,
        currentPanPosition: {
          lat: payload.lat,
          lng: payload.lng
        },
        currentDistanceFromCenter: distance(
          { lat, lng },
          {
            coordinates: {
              lat: payload.lat,
              lng: payload.lng
            }
          }
        )
      };
    },
    [HOVER_ITEM_ENTER](state, action) {
      debug(HOVER_ITEM_ENTER);
      const { id } = action;
      return {
        ...state,
        hoveredId: id
      };
    },
    [HOVER_ITEM_EXIT](state) {
      debug(HOVER_ITEM_EXIT);
      return {
        ...state,
        hoveredId: null
      };
    },
    [loadReviews.fulfilled](state, action) {
      debug(loadReviews.fulfilled);
      const { id, data, show } = action.payload;

      return {
        ...state,
        showReviews: show,
        reviews: {
          ...state.reviews,
          [id]: data === null ? [] : data
        }
      };
    },
    [loadPins.pending](state, action) {
      debug(loadPins.pending);
      const { coords, placeId, q } = action.meta.arg;

      return {
        ...state,
        loadPinsError: '',
        loadingPins: true,
        showReviews: false,
        pinResults: state.pinResults.length > 0 ? [] : state.pinResults,
        filterResults:
          state.filterResults.length > 0 ? [] : state.filterResults,
        metadata: {
          ...state.metadata,
          hasOccupancyData: false
        },
        hasSearched: {
          ...state.hasSearched,
          coords,
          placeId,
          q
        },
        currentPanPosition: {
          lat: coords.lat,
          lng: coords.lng
        }
      };
    },
    [loadPins.fulfilled](state, action) {
      debug(loadPins.fulfilled);
      const {
        payload: { data, meta }
      } = action;

      const filterResults = data.map((result) => ({
        ...result,
        distanceInMeters: milesToMeters(result.distance)
      }));

      return {
        ...state,
        loadingPins: false,
        metadata: meta,
        clientSideSearch: true,
        pinResults: data,
        filterResults,
        firstLoadComplete: true
      };
    },
    [loadPins.rejected](state, action) {
      const { error } = action;

      if (error?.name === 'AbortError') {
        // If pending request was aborted do nothing
        return state;
      }

      return { ...state, loadPinsError: action.payload, loadingPins: false };
    },
    [SET_MONTHLY_UPGRADE_MESSAGE](state, action) {
      debug(SET_MONTHLY_UPGRADE_MESSAGE);

      const { dates } = action;

      const monthlyUpgradeModal = getMonthlyUpgradeModalState(state, dates);

      return {
        ...state,
        monthlyUpgradeModal
      };
    },
    [DISMISS_MONTHLY_UPGRADE_MESSAGE](state) {
      return {
        ...state,
        monthlyUpgradeModal: MODAL_DISMISSED
      };
    },
    [loadWalkingDistance.pending](state) {
      return {
        ...state,
        walkingTimesLoaded: false
      };
    },
    [loadWalkingDistance.fulfilled](state, action) {
      debug(loadWalkingDistance.fulfilled, action);

      if (action.payload) {
        const walkingTimes = {};
        action.payload.forEach((item) => {
          walkingTimes[item.listingId] = item.time;
        });

        return {
          ...state,
          walkingTimes,
          walkingTimesLoaded: true
        };
      }

      return state;
    },
    [TOGGLE_FILTER_PANEL](state) {
      debug(TOGGLE_FILTER_PANEL);
      const {
        hasUpdatedFilterQuery,
        showFilterPanel,
        filterCheckboxes
      } = state;

      return {
        ...state,
        showFilterPanel: !showFilterPanel,
        hasUpdatedFilterQuery:
          hasUpdatedFilterQuery && !showFilterPanel
            ? false
            : hasUpdatedFilterQuery,
        filterCheckboxes: !showFilterPanel ? [] : filterCheckboxes
      };
    },
    [TOGGLE_FILTER_CHECKBOX](state, action) {
      debug(TOGGLE_FILTER_CHECKBOX, action);
      const value = action.payload;
      const filterCheckboxes = [...state.filterCheckboxes];
      const newFilters = toggleArray(filterCheckboxes, value);

      return {
        ...state,
        filterCheckboxes: newFilters
      };
    },
    [SET_FILTER_CHECKBOXES](state, action) {
      debug(SET_FILTER_CHECKBOXES, action);

      const filterCheckboxes = [...action.payload, ...state.filterCheckboxes];

      return {
        ...state,
        filterCheckboxes
      };
    },
    [CLEAR_FILTER_CHECKBOXES](state) {
      debug(CLEAR_FILTER_CHECKBOXES);

      return {
        ...state,
        filterCheckboxes: []
      };
    },
    [HAS_UPDATED_FILTER_QUERY](state) {
      debug(HAS_UPDATED_FILTER_QUERY);

      return {
        ...state,
        hasUpdatedFilterQuery: true
      };
    },
    [DISMISS_OCCUPANCY_SEARCH_KEY](state) {
      debug(DISMISS_OCCUPANCY_SEARCH_KEY);
      return {
        ...state,
        dismissedOccupancySearchKey: true
      };
    }
  }
});

export default searchSlice;

export const {
  setInstantBookMessage,
  dismissInstantBookMessage,
  reset
} = searchSlice.actions;

export const setMonthlyUpgradeMessage = ({ dates }): Action => ({
  dates,
  type: SET_MONTHLY_UPGRADE_MESSAGE
});

export const closeDetails = (): Action => ({ type: CLOSE_DETAILS });

export const dismissOccupancySearchKey = (): Action => ({
  type: DISMISS_OCCUPANCY_SEARCH_KEY
});

export const setPanned = (payload: { lat: number, lng: number }) => ({
  type: PANNED,
  payload
});

export const dismissMonthlyUpgradeMessage = (): Action => ({
  type: DISMISS_MONTHLY_UPGRADE_MESSAGE
});

export function onMouseOver(type: string, id: number) {
  return (dispatch: (*) => Promise<*>) => {
    dispatch({ type: HOVER_ITEM_ENTER, id });
  };
}

export function onMouseOut() {
  return (dispatch: (*) => Promise<*>) => {
    dispatch({ type: HOVER_ITEM_EXIT });
  };
}

export const toggleFilterPanel = makeActionCreator(TOGGLE_FILTER_PANEL);
export const toggleFilterCheckbox = makeActionCreator(
  TOGGLE_FILTER_CHECKBOX,
  'payload'
);
export const setFilterCheckboxes = makeActionCreator(
  SET_FILTER_CHECKBOXES,
  'payload'
);
export const clearFilterCheckbox = makeActionCreator(CLEAR_FILTER_CHECKBOXES);
export const hasUpdatedFilterQuery = makeActionCreator(
  HAS_UPDATED_FILTER_QUERY
);

export const isMonthlyUpgradeModalVisible = (state: { search: State }) =>
  state.search.monthlyUpgradeModal === MONTHLY_UPGRADE_MODAL_VISIBLE ||
  state.search.monthlyUpgradeModal === MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE;

export const selectIsMonthlyAutoUpgraded = (state: { search: State }) =>
  state.search.monthlyUpgradeModal === MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE;

export const selectIsInstantBookVisible = (state: { search: State }) =>
  state.search.instantBookModal === MODAL_VISIBLE;

export const selectInstantBookPrice = (state: { search: State }) =>
  state.search.filterResults?.[0]?.price.formatted || '';

export const selectInstantBookRating = (state: { search: State }) =>
  state.search.filterResults?.[0]?.reviews?.rating || '';

export const selectInstantBookWalkingDistance = (state: { search: State }) =>
  state.search.walkingTimes[state.search.filterResults?.[0]?.id]
    ? formatDurationFromSeconds(
        Math.max(
          60,
          state.search.walkingTimes[state.search.filterResults[0].id]
        )
      )
    : '';

export const selectInstantBookMilesDistance = (state: { search: State }) =>
  `${state.search.filterResults?.[0]?.distance} miles` || 'null';

export const selectInstantBookListingType = (state: { search: State }) =>
  state.search.filterResults?.[0]?.type || '';

export const selectInstantBookId = (state: { search: State }) =>
  state.search.filterResults?.[0]?.id || null;

export const selectInstantBookIsReservable = (state: { search: State }) => {
  const listing = state.search.filterResults?.[0];

  const reservable = isReservable(listing);
  const unavailable = listing?.unavailable;

  if (reservable && unavailable) {
    return true;
  }

  return reservable;
};

const selectSearchHasOccupancyData = (state) =>
  state.search?.metadata?.hasOccupancyData || false;

export const selectSearchHasSearchedCoordinates = (state) =>
  state.search.hasSearched.coords;

export const selectSearchHasSearchedQuery = (state) =>
  state.search.hasSearched.q || '';

const selectSearchHasDismissedOccupancyKey = (state) =>
  state.search.dismissedOccupancySearchKey;

const selectSearchResults = (state) => state.search.filterResults;

const selectSearchResultsLatLngBounds = createSelector(
  selectSearchResults,
  selectSearchHasSearchedCoordinates,
  (results, searchCoordinates) => {
    if (results.length === 0) {
      return null;
    }
    try {
      // eslint-disable-next-line no-undef
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(searchCoordinates);

      results.forEach((result) => bounds.extend(result.coordinates));

      return bounds;
    } catch (e) {
      return null;
    }
  }
);

export const selectSearchWalkingTimes = (state) => state.search?.walkingTimes;

const selectSearchCurrentPanPosition = (state) =>
  state.search?.currentPanPosition;

const selectSearchCurrentDistanceFromCenter = (state) =>
  state.search?.currentDistanceFromCenter;

export const selectSearchIsLoading = (state) => state.search.loadingPins;

// This will control if the redo search button is shown
export const selectShowRedoSearchButton = createSelector(
  selectSearchResultsLatLngBounds,
  selectSearchCurrentPanPosition,
  selectSearchCurrentDistanceFromCenter,
  selectSearchIsLoading,
  (bounds, currentLatLng, currentDistanceFromCenter, isLoading) => {
    if (isLoading) {
      return false;
    }

    if (bounds !== null) {
      return bounds.contains(currentLatLng) === false;
    }

    return currentDistanceFromCenter > 1.25;
  }
);

export const selectShowOccupancyKey = createSelector(
  selectShowRedoSearchButton,
  selectSearchHasOccupancyData,
  selectSearchHasDismissedOccupancyKey,
  (showRedoSearch, hasOccupancy, keyDismissed) => {
    if (showRedoSearch) {
      return false;
    }

    return hasOccupancy && !keyDismissed;
  }
);

export const selectSearchHasNoResults = (state) =>
  state.search.loadingPins === false && state.search.filterResults.length === 0;

export const selectSearchLoadPinsError = (state) => state.search.loadPinsError;

export const selectSearchReviews = (state) => state.search.reviews;

export const selectSearchPinResults = (state) => state.search.pinResults;
export const selectSearchFocused = (state) => state.search.focusedLocation;
export const selectSearchShowFilterPanel = (state) =>
  state.search.showFilterPanel;

export const selectSearchHoveredId = (state) => state.search.hoveredId;

const select20MinWalkingBoundingBoxFromCenter = createSelector(
  selectSearchHasSearchedCoordinates,
  (searchCenter) => {
    const time = 20;
    const centerLatLng = new google.maps.LatLng(searchCenter);

    const circle = new google.maps.Circle({
      center: centerLatLng,
      radius: WALKING_METERS_PER_MIN * time
    });

    return circle.getBounds();
  }
);

export const selectHasPinResultsWithinWalkingRadiusBounds = createSelector(
  selectSearchPinResults,
  select20MinWalkingBoundingBoxFromCenter,
  (results, bounds) => {
    if (bounds === undefined) {
      return false;
    }

    const resultInBounds = results.find((result) => {
      const resultCoords = new google.maps.LatLng(result.coordinates);
      return bounds.contains(resultCoords);
    });

    return resultInBounds !== undefined;
  }
);

export const selectSearchBoundingBoxOfBestPins = createSelector(
  selectSearchPinResults,
  selectSearchHasSearchedCoordinates,
  (pins, searchPosition) => {
    if (pins.length === 0) {
      return null;
    }

    const bounds = new google.maps.LatLngBounds(searchPosition, searchPosition);

    const pinsByDistance = [...pins].sort(
      (a, b) => parseFloat(a.distance) - parseFloat(b.distance)
    );

    const bestPins = [];

    for (let i = 0; i < pinsByDistance.length; i += 1) {
      if (bestPins.length === 3) {
        break;
      }

      const { availableFrom, unavailable } = pinsByDistance[i];

      if (!availableFrom && !unavailable) {
        bestPins.push(pinsByDistance[i]);
      }
    }

    if (bestPins.length === 0) {
      return null;
    }

    bestPins.forEach((pin) => {
      const resultCoords = new google.maps.LatLng(pin.coordinates);
      bounds.extend(resultCoords);
    });

    const checkPoints = [bounds.getNorthEast(), bounds.getSouthWest()];

    // Find the point on the bounding box that is furthest away from the center
    const furthestItems = checkPoints
      .map((latLng) => ({
        distance: google.maps.geometry.spherical.computeDistanceBetween(
          searchPosition,
          latLng
        ),
        latLng
      }))
      .sort((a, b) => b.distance - a.distance)[0];

    const furthestDistance = furthestItems.distance;
    const furthestLatLng = furthestItems.latLng;

    const headingOfLargestDistance = google.maps.geometry.spherical.computeHeading(
      searchPosition,
      furthestLatLng
    );

    const centeredBounds = new google.maps.LatLngBounds(
      searchPosition,
      searchPosition
    );

    for (let i = 0; i < 4; i += 1) {
      const computedLatLng = google.maps.geometry.spherical.computeOffset(
        searchPosition,
        furthestDistance,
        headingOfLargestDistance + 90 * i
      );

      centeredBounds.extend(computedLatLng);
    }

    return centeredBounds;
  }
);

export const selectSearchPinsWithoutUnavailable = createSelector(
  selectSearchResults,
  (results) => results.filter(({ unavailable }) => !unavailable)
);

export const selectFirstLoadComplete = (state) =>
  state.search.firstLoadComplete;
export const selectWalkingTimesLoaded = (state) =>
  state.search.walkingTimesLoaded;

export const analyticsValueSubscribers = {
  pinResults: [
    (state) => state.search.pinResults,
    ({ value }) => {
      if (value.length === 0) {
        return;
      }

      let countWithin300 = 0;

      const productItemsArray = value.map((item) => {
        const { unavailable } = item;

        const distanceInMeters = Math.round(
          parseFloat(item.distance) * 1609.34
        );

        if (!unavailable && distanceInMeters <= 300) {
          countWithin300 += 1;
        }

        return {
          item_id: item.id,
          item_name: item.title,
          distance: distanceInMeters,
          unavailable
        };
      });

      gtagTrack('srp_items', {
        resultsIn300Meters: countWithin300,
        items: productItemsArray
      });
    }
  ]
};
